/**
 * Super Admin · DDR Health Lens - cross-tenant operational dashboard.
 *
 * Visible only to platform_super_admin / platform_ops / platform_support.
 * Login as 'admin@demo.foctta' to see live data; everyone else gets
 * a friendly access-denied placeholder.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Shield, Building2, AlertTriangle, Activity, GitBranch, Library } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  getSuperAdminHealth,
  type CrossTenantHealth,
  type TenantHealth,
} from '@/lib/api';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';

export function SuperAdminDdrHealth(): JSX.Element {
  const [data, setData] = useState<CrossTenantHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSuperAdminHealth()
      .then((d) => {
        if (cancelled) return;
        setData(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'unknown');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <PageContainer>
      <PageHeader
        title="Super Admin · DDR Health Lens"
        subtitle="Cross-tenant aggregate observability for FOCTTA platform operations. Tenant-scoped data is read with RLS bypass under a platform_super_admin actor - no per-actor raw queries; only aggregates."
        actions={<FocttaCoreBadge label="Live tenants" />}
        badges={[
          { label: 'Platform-ops only', variant: 'glass' },
          { label: data ? `${data.tenantCount} tenants` : '-', variant: 'info' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">
            Access denied: {error}. This lens requires platform_super_admin / platform_ops / platform_support actor role.
          </p>
        </GlassCard>
      )}

      {loading && !error && (
        <GlassCard className="p-6 mb-4">
          <p className="text-xs text-muted-foreground">Loading cross-tenant health…</p>
        </GlassCard>
      )}

      {data && (
        <>
          {/* KPIs roll-up */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Decisions (24h)" value={data.totals.decisions24h} icon={Activity} hint={`${data.tenantCount} tenants`} />
            <KpiCard label="Blocks (24h)" value={data.totals.blocks24h} icon={Shield} glow="medium" />
            <KpiCard label="Open findings" value={data.totals.findingsOpen} icon={AlertTriangle} />
            <KpiCard label="Breach incidents open" value={data.totals.breachesOpen} icon={AlertTriangle} glow={data.totals.breachesOpen > 0 ? 'high' : 'low'} />
          </div>

          {/* Fleet alerts */}
          <GlassCard className="p-6 mb-6">
            <CardHeader className="p-0 mb-4">
              <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning" /> Fleet alerts</CardTitle>
              <CardDescription>Cross-tenant signals that suggest a coordinated pattern</CardDescription>
            </CardHeader>
            <div className="space-y-2">
              {data.fleetAlerts.map((a, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass-lite p-3 rounded-md flex items-start gap-3"
                >
                  <span className={`h-2 w-2 rounded-full mt-1.5 ${a.severity === 'critical' ? 'bg-sev-critical animate-pulse' : a.severity === 'high' ? 'bg-warning' : 'bg-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm">{a.title}</span>
                      <Badge variant={a.severity === 'critical' ? 'critical' : a.severity === 'high' ? 'warning' : 'glass'} className="text-[9px]">{a.severity}</Badge>
                      <Badge variant="info" className="text-[9px]">{a.affectedTenantCount} tenant{a.affectedTenantCount === 1 ? '' : 's'}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{a.rationale}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </GlassCard>

          {/* Per-tenant grid */}
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Per-tenant health</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.tenants.map((t: TenantHealth) => (
              <GlassCard key={t.tenantId} className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <Badge variant={t.licenseTier === 'sovereign' ? 'success' : 'info'} className="text-[9px]">{t.licenseTier}</Badge>
                </div>
                <CardTitle className="text-sm leading-tight">{t.tenantName}</CardTitle>
                <p className="text-[10px] text-muted-foreground">{t.industryAnchor.replace(/_/g, ' ')}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border/30">
                  <div><div className="text-[9px] uppercase text-muted-foreground">Decisions/24h</div><div className="text-lg font-display tabular">{t.decisions24h.toLocaleString('en-IN')}</div></div>
                  <div><div className="text-[9px] uppercase text-muted-foreground text-sev-critical">Blocks/24h</div><div className="text-lg font-display tabular text-sev-critical">{t.blocks24h}</div></div>
                  <div><div className="text-[9px] uppercase text-muted-foreground">Findings open</div><div className="text-lg font-display tabular">{t.findingsOpen}</div></div>
                  <div><div className="text-[9px] uppercase text-muted-foreground">UEBA alerts</div><div className="text-lg font-display tabular">{t.uebaOpenAlerts}</div></div>
                </div>
                <div className="mt-3 pt-3 border-t border-border/30">
                  <div className="text-[9px] uppercase text-muted-foreground">Top rule firing</div>
                  <code className="text-[10px] font-mono opacity-80">{t.topRuleFiring}</code>
                </div>
              </GlassCard>
            ))}
          </div>
        </>
      )}
    </PageContainer>
  );
}
